import React from "react";
import { motion } from "framer-motion";
import { Code, Smartphone, ShoppingCart, Cloud, Database, Shield, Zap } from "lucide-react";

const Services: React.FC = () => {
  const services = [
    {
      icon: Code,
      title: "Custom Web Development",
      description: "Tailored web applications built with modern frameworks and best practices.",
      features: ["React/Next.js", "Node.js/Express", "Responsive Design", "SEO Optimization"],
    },
    {
      icon: Smartphone,
      title: "Mobile App Development",
      description: "Native and cross-platform mobile applications for iOS and Android.",
      features: ["React Native", "Flutter", "Native iOS/Android", "App Store Optimization"],
    },
    {
      icon: ShoppingCart,
      title: "E-commerce Solutions",
      description: "Complete online store solutions with payment processing and inventory management.",
      features: ["Shopify/WooCommerce", "Payment Integration", "Inventory Management", "Analytics"],
    },
    {
      icon: Cloud,
      title: "Cloud & DevOps",
      description: "Scalable cloud infrastructure and automated deployment pipelines.",
      features: ["AWS/Azure/GCP", "Docker & Kubernetes", "CI/CD Pipelines", "Monitoring"],
    },
    {
      icon: Database,
      title: "Database Design",
      description: "Optimized database architecture and data management solutions.",
      features: ["SQL/NoSQL", "Data Modeling", "Performance Optimization", "Backup & Recovery"],
    },
    {
      icon: Shield,
      title: "Security & Compliance",
      description: "Comprehensive security measures and compliance solutions.",
      features: ["SSL Certificates", "Data Encryption", "GDPR Compliance", "Security Audits"],
    },
  ];

  const process = [
    {
      step: "01",
      title: "Discovery & Planning",
      description: "We start by understanding your business goals, requirements, and target audience.",
    },
    {
      step: "02",
      title: "Design & Prototyping",
      description: "Our designers create wireframes and prototypes to visualize the user experience.",
    },
    {
      step: "03",
      title: "Development",
      description: "Our developers build your solution using modern technologies and best practices.",
    },
    {
      step: "04",
      title: "Testing & Quality Assurance",
      description: "We thoroughly test your solution to ensure it meets all requirements and standards.",
    },
    {
      step: "05",
      title: "Deployment & Launch",
      description: "We deploy your solution and provide training and support for your team.",
    },
    {
      step: "06",
      title: "Maintenance & Support",
      description: "We provide ongoing maintenance, updates, and support to keep your solution running smoothly.",
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section bg-gradient-to-br from-blue-50 to-white">
        <div className="container text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-5xl lg:text-6xl font-bold text-dark-gray mb-6">Our Services</h1>
            <p className="text-xl text-medium-gray max-w-3xl mx-auto leading-relaxed">
              We offer comprehensive digital solutions to help your business thrive in the modern digital landscape.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-dark-gray mb-4">What We Offer</h2>
            <p className="text-xl text-medium-gray max-w-2xl mx-auto">From web development to mobile apps, we provide end-to-end solutions.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="bg-white rounded-xl p-6 shadow-medium hover:shadow-heavy transition-shadow border border-gray-100">
                  <div className="flex justify-center mb-4">
                    <service.icon size={48} className="text-primary-blue" />
                  </div>
                  <h3 className="text-xl font-semibold text-dark-gray mb-3 text-center">{service.title}</h3>
                  <p className="text-medium-gray mb-4 text-center">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-medium-gray">
                        <Zap size={16} className="text-primary-blue mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Development Process */}
      <section className="section bg-light-gray">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-dark-gray mb-4">Our Development Process</h2>
            <p className="text-xl text-medium-gray max-w-2xl mx-auto">A proven methodology that ensures successful project delivery.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="bg-white rounded-xl p-6 shadow-medium hover:shadow-heavy transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-primary-blue text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg mr-4">
                      {step.step}
                    </div>
                    <h3 className="text-xl font-semibold text-dark-gray">{step.title}</h3>
                  </div>
                  <p className="text-medium-gray">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-gradient-primary">
        <div className="container text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h2 className="text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Let's discuss your project requirements and create a custom solution that drives your business forward.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="btn btn-white text-lg px-8 py-4">
                Get Free Quote
              </a>
              <a href="/contact" className="btn btn-outline-white text-lg px-8 py-4">
                Schedule Consultation
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
